import type { BobState, PromptFormat } from "../types";
import {
  exportBuildPlanMd,
  exportFullStateJson,
  exportHandoffPrompt,
  exportMergePlanJson,
  exportRepoAuditJson,
} from "../lib/engine/exportCenter";
import { downloadText } from "../lib/storage";
import { forgePrompt } from "../lib/engine/promptForge";

interface Props {
  state: BobState;
  toast: (msg: string) => void;
}

export function ExportCenterPanel({ state, toast }: Props) {
  const latestGrade = state.graderResults[state.graderResults.length - 1];
  const latestPrompt = state.prompts[state.prompts.length - 1];
  const latestMerge = state.mergePlans[state.mergePlans.length - 1];
  const latestIntake = state.intakes[state.intakes.length - 1];

  function exportMd() {
    downloadText("bob-build-plan.md", exportBuildPlanMd(state), "text/markdown");
    toast("Downloaded bob-build-plan.md");
  }

  function exportAuditJson() {
    downloadText("bob-repo-audit.json", exportRepoAuditJson(latestGrade), "application/json");
    toast("Downloaded bob-repo-audit.json");
  }

  function exportPrompt(format: PromptFormat, filename: string) {
    const prompt =
      latestPrompt?.format === format
        ? latestPrompt
        : forgePrompt(
            { intent: latestIntake?.projectName ?? "Continue BOB build", format },
            latestIntake,
          );
    downloadText(filename, exportHandoffPrompt(prompt), "text/markdown");
    toast(`Downloaded ${filename}`);
  }

  function exportState() {
    downloadText("bob-state-export.json", exportFullStateJson(state), "application/json");
    toast("Downloaded full state JSON");
  }

  function exportMerge() {
    downloadText("bob-merge-plan.json", exportMergePlanJson(latestMerge), "application/json");
    toast("Downloaded merge plan JSON");
  }

  return (
    <section className="surface glass-panel">
      <h2>Export Center</h2>
      <p className="lead">Download handoff artifacts — all generated locally in-browser.</p>
      <div className="btn-row">
        <button type="button" className="btn" onClick={exportMd}>
          Export build plan (.md)
        </button>
        <button type="button" className="btn" onClick={exportAuditJson}>
          Export repo audit (.json)
        </button>
        <button type="button" className="btn secondary" onClick={() => exportPrompt("cursor", "bob-cursor-handoff.md")}>
          Export Cursor handoff
        </button>
        <button type="button" className="btn secondary" onClick={() => exportPrompt("claude-code", "bob-claude-handoff.md")}>
          Export Claude Code handoff
        </button>
        <button type="button" className="btn secondary" onClick={() => exportPrompt("lovable", "bob-lovable-handoff.md")}>
          Export Lovable handoff
        </button>
        <button type="button" className="btn secondary" onClick={exportMerge}>
          Export merge plan (.json)
        </button>
        <button type="button" className="btn gold" onClick={exportState}>
          Export full state (.json)
        </button>
      </div>
    </section>
  );
}
